import { Localization } from "@viamap/viamap2-common"
import { Fragment, useContext, useRef, useState } from "react"
import { BsList, BsTrash } from "react-icons/bs"
import { GlassButton } from "src/components/GlassButtons"
import { ApplicationStateContext } from "src/states/ApplicationState"
import { Feature } from "src/states/ApplicationStateFeatures"

type favBoxes = {
  [favName: string]: string[]
}

export type FaveBoxKeys = "Ortofoto" | "Område" | "Arealer" | "Højder" | "Handelsoplysninger" | "Ejerforhold" | "Samlet Fast Ejendom" | "Mat Oversigt" | "Matrikler" | "Matrikler Seperat" | "BBR Oversigt" | "BBR" | "BBR Teknik" | "Tingbog - Øvrigt" | "Tingbog - Hæftelser" | "Tingbog - Servitutter" | "Tingbog - Adkomster" | "Virksomheder på Ejendom" | "Vurd 2020" | "Vurd 2019" | "Vurd 2018" | "Vurd 2017" | "Vurd 2012" | "Lokalplaner" | "Kommuneplan" | "Energimærkning" | "Marker" | "Markerkort" | "Fælleslodder" | "Aflyste Kommuneplaner" | "Aflyste Lokalplaner" | "Delområder" | "Dokumenter"

const BoxList: readonly { group: FaveBoxKeys, feature?: Feature }[] = [
  { group: "Ortofoto" },
  { group: "Område" },
  { group: "Arealer" },
  { group: "Højder" },
  { group: "Handelsoplysninger" },
  { group: "Ejerforhold" },
  { group: "Samlet Fast Ejendom" },
  { group: "Mat Oversigt", feature: Feature.KortUdsnitEjendom },
  { group: "Matrikler" },
  { group: "Matrikler Seperat" },
  { group: "BBR Oversigt", feature: Feature.KortUdsnitEjendom },
  { group: "BBR" },
  { group: "BBR Teknik" },
  { group: "Tingbog - Hæftelser" },
  { group: "Tingbog - Servitutter" },
  { group: "Tingbog - Adkomster" },
  { group: "Tingbog - Øvrigt" },
  { group: "Virksomheder på Ejendom" },
  { group: "Vurd 2020" },
  { group: "Vurd 2019" },
  { group: "Vurd 2018" },
  { group: "Vurd 2017" },
  { group: "Vurd 2012" },
  { group: "Lokalplaner" },
  { group: "Kommuneplan" },
  { group: "Energimærkning" },
  { group: "Marker" },
  { group: "Markerkort" },
  { group: "Fælleslodder" },
  { group: "Aflyste Kommuneplaner" },
  { group: "Aflyste Lokalplaner" },
  { group: "Delområder" },
  { group: "Dokumenter" }
] as const


export function FavEditor(props: {
  show: boolean,
  favBoxes: favBoxes,
  setFavBoxes: (favBoxes: favBoxes) => void,
  onClose: () => void
}) {
  const { hasAccessToFeature } = useContext(ApplicationStateContext)
  const conRef = useRef<HTMLDivElement>(null)

  function dragElement(key: string, idx: number) {
    let x = window.structuredClone(props.favBoxes);
    let lastIDX = x["minFavorit"].findIndex((a) => a == key)
    x["minFavorit"] = x["minFavorit"].filter((a) => a !== key)
    x["minFavorit"].splice(lastIDX > idx ? idx : Math.max(0, idx - 1), 0, key)
    props.setFavBoxes(x)
  }

  function deleteBox(key: string) {
    let x = window.structuredClone(props.favBoxes);
    x["minFavorit"] = x["minFavorit"].filter((a) => a !== key)
    props.setFavBoxes(x)
  }

  function addBox(key: string) {
    let x = window.structuredClone(props.favBoxes);
    x["minFavorit"] = [...x["minFavorit"], key]
    props.setFavBoxes(x)
  }

  if (!props.show) {
    return <></>
  }

  return (
    <div className="ModernBox" style={{
      left: "50%",
      transform: "translateX(-50%)",
      top: "25%",
      position: "absolute",
      backgroundColor: "white",
      borderRadius: "8px",
      minWidth: "320px",
      maxWidth: "400px",
      minHeight: "350px",
      maxHeight: "450px",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
      zIndex: "1000",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    }}>
      {/* Header */}
      <div style={{
        padding: "12px 16px",
        borderBottom: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb"
      }}>
        <h4 style={{
          margin: 0,
          fontSize: "16px",
          fontWeight: "600",
          color: "#111827",
          textAlign: "center"
        }}>
          {Localization.getText("Favorite")}
        </h4>
      </div>

      {/* Content Area */}
      <div style={{ padding: "12px 16px", flex: 1, overflow: "auto" }}>
        <div ref={conRef} className="FavItemCon"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            marginBottom: "12px"
          }}
        >
          {props.favBoxes["minFavorit"].map((a, idx) => {
            return (
              <Fragment key={a}>
                <DropArea areaNr={idx} droppedKey={(key) => dragElement(key, idx)} />
                <FavItem key={a} favKey={a} onDelete={() => deleteBox(a)} />
              </Fragment>
            )
          })}
          <DropArea areaNr={-1} droppedKey={(key) => dragElement(key, props.favBoxes["minFavorit"].length)} />
        </div>

        {/* Add New Item */}
        <div style={{ marginBottom: "12px" }}>
          <label style={{
            display: "block",
            marginBottom: "4px",
            fontSize: "12px",
            fontWeight: "500",
            color: "#374151"
          }}>
            {Localization.getText("Add")}:
          </label>
          <select
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "white",
              fontSize: "12px",
              color: "#374151",
              outline: "none",
              transition: "border-color 0.2s ease"
            }}
            value={""}
            onChange={(ev) => addBox(ev.target.value)}
            onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
            onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
          >
            <option disabled value={""}>-- {Localization.getText("Select to add")} --</option>
            {BoxList.filter((a) => !props.favBoxes["minFavorit"].includes(a.group) && (a.feature == undefined || hasAccessToFeature(a.feature))).map((val) => {
              return <option value={val.group} key={val.group}>{val.group}</option>
            })}
          </select>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: "8px 16px",
        borderTop: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb",
        display: "flex",
        justifyContent: "flex-end"
      }}>
        <GlassButton onClick={() => props.onClose()}>
          {Localization.getText("Close")}
        </GlassButton>
      </div>
    </div>
  )
}

function DropArea(props: {
  areaNr: number,
  droppedKey: (key: string) => void,
}) {
  const [drag, setDrag] = useState(false)

  return <div
    onDragEnter={(e) => {
      e.preventDefault();
      setDrag(true);
    }}
    onDragOver={(e) => {
      e.preventDefault();
    }}
    onDragLeave={(e) => {
      // Only set drag to false if we're actually leaving the drop area
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setDrag(false);
      }
    }}
    onDrop={(ev) => {
      ev.preventDefault();
      let favKey = ev.dataTransfer.getData("text/plain")
      if (favKey) {
        props.droppedKey(favKey)
      }
      setDrag(false)
    }}
    className={"DropArea " + (drag ? " Act" : "")}
    style={{
      height: drag ? "30px" : "2px",
      backgroundColor: drag ? "#86beba" : "transparent",
      borderRadius: "4px",
      transition: "all 0.2s ease",
      margin: drag ? "2px 0" : "1px 0",
      border: drag ? "2px dashed #334b49" : "none",
      opacity: drag ? 1 : 0.3
    }}
  />
}

function FavItem(props: {
  favKey: string;
  onDelete?: () => void;
  onDrag?: () => void;
}) {
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  return (
    <div
      draggable={true}
      className={"FavItem"}
      onDragStart={(ev) => {
        ev.dataTransfer.setData("text/plain", props.favKey);
        ev.dataTransfer.effectAllowed = "move";
        setIsDragging(true);
      }}
      onDragEnd={() => {
        setIsDragging(false);
      }}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        backgroundColor: isDragging ? "#dbfae5" : (mouseOver ? "#f3f4f6" : "white"),
        border: isDragging ? "2px solid #334b49" : "1px solid #e5e7eb",
        borderRadius: "6px",
        cursor: mouseOver ? "grab" : "default",
        transition: "all 0.2s ease",
        boxShadow: isDragging ? "0 4px 12px rgba(0, 0, 0, 0.15)" : (mouseOver ? "0 2px 8px rgba(0, 0, 0, 0.1)" : "0 1px 3px rgba(0, 0, 0, 0.1)"),
        gap: "8px",
        opacity: isDragging ? 0.9 : 1,
        transform: isDragging ? "scale(1.02)" : "none"
      }}
    >
      {/* Drag Handle */}
      <div
        className={"ItemHandle"}
        style={{
          color: "#6b7280",
          cursor: "grab",
          padding: "2px",
          borderRadius: "4px",
          transition: "color 0.2s ease",
          fontSize: "14px",
          display: "flex",
          alignItems: "center"
        }}
      >
        <BsList />
      </div>

      {/* Item Name */}
      <div style={{
        flex: 1,
        fontSize: "13px",
        fontWeight: "500",
        color: "#374151"
      }}>
        {props.favKey}
      </div>

      {/* Delete Button */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          props.onDelete?.();
        }}
        style={{
          color: "#ef4444",
          cursor: "pointer",
          padding: "4px",
          borderRadius: "4px",
          transition: "all 0.2s ease",
          fontSize: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#fee2e2";
          e.currentTarget.style.color = "#dc2626";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "#ef4444";
        }}
      >
        <BsTrash />
      </div>
    </div>
  )
}